import { useMemo, useRef } from "react";

import { CalendarDate, createCalendar } from "@internationalized/date";
import {
  type AriaCalendarProps,
  type DateValue,
  type Key,
  useCalendar,
  useLocale,
} from "react-aria";
import { Item, useCalendarState } from "react-stately";

import { Button } from "#components/Button";
import { CaretRightIcon } from "#icons";

import { CalendarGrid } from "./CalendarGrid";
import { Select } from "./Select";

interface CalendarProps<T extends DateValue> extends AriaCalendarProps<T> {
  yearOptions?: number[];
}
export function Calendar<T extends DateValue>({ yearOptions, ...props }: CalendarProps<T>) {
  const { locale } = useLocale();
  const state = useCalendarState({
    ...props,
    locale,
    createCalendar,
  });

  const currentMonth = state.focusedDate.month;
  const currentYear = state.focusedDate.year;

  const todayYear = new Date().getFullYear();

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const years = useMemo(
    () => yearOptions ?? Array.from({ length: 21 }, (_, i) => todayYear - 10 + i),
    [yearOptions, todayYear],
  );

  const ref = useRef(null);
  const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(props, state);

  function handleMonthChange(key: Key | null) {
    if (!key) return;
    state.setFocusedDate(new CalendarDate(currentYear, Number(key), 1));
  }

  function handleYearChange(key: Key | null) {
    if (!key) return;
    state.setFocusedDate(new CalendarDate(Number(key), currentMonth, 1));
  }

  return (
    <div {...calendarProps} className="inline-block" ref={ref}>
      <header className="flex w-full items-center justify-between gap-1 pb-xs">
        <Button {...prevButtonProps} className="px-0 hover:text-brand-primary" variant="plain">
          <CaretRightIcon className="size-lg rotate-180" />
        </Button>
        <Select
          aria-label="date-picker-month-select"
          onSelectionChange={handleMonthChange}
          placeholder="Month"
          selectedKey={String(currentMonth)}
        >
          {months.map((month) => (
            <Item key={month}>
              {new Intl.DateTimeFormat(locale, { month: "short" }).format(
                new Date(2000, month - 1, 1),
              )}
            </Item>
          ))}
        </Select>
        <Select
          aria-label="date-picker-year-select"
          onSelectionChange={handleYearChange}
          placeholder="Year"
          selectedKey={String(currentYear)}
        >
          {years.map((year) => (
            <Item key={year} textValue={`${year}`}>
              {year}
            </Item>
          ))}
        </Select>

        <Button {...nextButtonProps} className="px-0 hover:text-brand-primary" variant="plain">
          <CaretRightIcon className="size-lg" />
        </Button>
      </header>
      <CalendarGrid state={state} />
    </div>
  );
}
